import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import i18n from '../i18n';
import { getBaseUrl } from '../utils/urlbase';

const apiUrl = getBaseUrl();

// Callback para manejar logout cuando la sesión expire (401)
let authLogoutCallback: (() => void) | null = null;
let logoutInProgress = false;

export function setAuthLogoutCallback(callback: () => void) {
  authLogoutCallback = callback;
  logoutInProgress = false; // Reset flag when a new callback is set (new session)
}

export function clearAuthLogoutCallback() {
  authLogoutCallback = null;
  logoutInProgress = false;
}

function generateCorrelationId(): string {
  // RFC4122 v4 simple
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const http: AxiosInstance = axios.create({
  baseURL: apiUrl,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: false,
});

// Interceptor de solicitud
http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const lang = i18n?.language || 'es';
  if (!config.headers) {
    // En axios v1, headers siempre debe existir, pero protegemos por si acaso
    (config as any).headers = {};
  }
  try {
    // AxiosHeaders tiene método set
    (config.headers as any).set?.('Accept-Language', lang);
    (config.headers as any).set?.('X-Correlation-Id', generateCorrelationId());
  } catch {
    (config.headers as any)['Accept-Language'] = lang;
    (config.headers as any)['X-Correlation-Id'] = generateCorrelationId();
  }
  return config;
});

// Interceptor de respuesta
http.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    // Normalizar error
    const status = error.response?.status;
    const data: any = error.response?.data || {};
    const message =
      data?.mensaje ||
      data?.Mensaje ||
      data?.error ||
      data?.detalle ||
      data?.detail ||
      data?.message ||
      error.message ||
      'Error de red';

    return Promise.reject({
      status,
      message,
      code: data?.codigo ?? data?.Codigo ?? status,
      data: error.response?.data,
      isAxiosError: true,
    });
  }
);

/**
 * Crea un cliente HTTP autenticado que incluye el token Bearer automáticamente.
 * @param getToken Función que retorna el token JWT actual o null
 */
export function createAuthenticatedClient(getToken: () => string | null): AxiosInstance {
  const authenticatedHttp = axios.create({
    baseURL: apiUrl,
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    withCredentials: false,
  });

  // Interceptor de solicitud con token Bearer
  authenticatedHttp.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const lang = i18n?.language || 'es';
    const token = getToken();
    const correlationId = generateCorrelationId();
    
    try {
      (config.headers as any).set?.('Accept-Language', lang);
      (config.headers as any).set?.('X-Correlation-Id', correlationId);
      if (token) {
        (config.headers as any).set?.('Authorization', `Bearer ${token}`);
      }
    } catch {
      (config.headers as any)['Accept-Language'] = lang;
      (config.headers as any)['X-Correlation-Id'] = correlationId;
      if (token) {
        (config.headers as any)['Authorization'] = `Bearer ${token}`;
      }
    }
    
    // Log de request para debugging
    console.log(`[HTTP ${config.method?.toUpperCase()}] ${config.url}`, {
      correlationId,
      hasAuth: !!token,
      payload: config.data ? JSON.stringify(config.data) : undefined
    });
    
    return config;
  });

  // Interceptor de respuesta con manejo de 401 (sesión expirada)
  authenticatedHttp.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      const status = error.response?.status;
      const data: any = error.response?.data || {};
      const message =
        data?.mensaje ||
        data?.Mensaje ||
        data?.error ||
        data?.detalle ||
        data?.detail ||
        data?.message ||
        error.message ||
        'Error de red';

      // Si es 401 (no autorizado/sesión expirada), loggear y ejecutar logout callback con delay
      if (status === 401) {
        console.warn('[HTTP 401] Sesión expirada o token inválido:', {
          status,
          message,
          responseData: data,
          url: error.config?.url
        });
        
        if (authLogoutCallback && !logoutInProgress) {
          logoutInProgress = true;
          console.warn('[HTTP] Iniciando logout por 401 (guard activo para evitar loop)');
          // Delay para que los logs aparezcan antes de la recarga
          setTimeout(() => {
            authLogoutCallback?.();
          }, 500);
        }
      }

      return Promise.reject({
        status,
        message,
        code: data?.codigo ?? data?.Codigo ?? status,
        data: error.response?.data,
        isAxiosError: true,
      });
    }
  );

  return authenticatedHttp;
}

export default http;


